import { useEffect, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Card, Input, Button, Spinner, PageSkeleton } from '@daycal/ui';
import { api } from '../../lib/apiClient.js';

// Platform settings (architecture.md §24 M10). Site title + default locale + SMTP.
// The SMTP password is write-only: the API returns `smtp_password_set` (not the value),
// so we only send a new password when the admin types one.
export default function AdminSettings() {
  const { data, isLoading } = useQuery({ queryKey: ['admin-settings'], queryFn: api.adminGetSettings });
  const [form, setForm] = useState({
    site_title: '', default_locale: 'en',
    smtp_host: '', smtp_port: '', smtp_user: '', smtp_password: '',
  });
  const [passwordSet, setPasswordSet] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    const s = data?.settings;
    if (s) {
      setForm((f) => ({
        ...f,
        site_title: s.site_title || '',
        default_locale: s.default_locale || 'en',
        smtp_host: s.smtp_host || '',
        smtp_port: s.smtp_port ?? '',
        smtp_user: s.smtp_user || '',
        smtp_password: '',
      }));
      setPasswordSet(!!s.smtp_password_set);
    }
  }, [data]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = useMutation({
    mutationFn: () => {
      // Omit the password unless the admin entered a new one (empty = leave unchanged).
      const { smtp_password, ...rest } = form;
      const body = smtp_password ? { ...rest, smtp_password } : rest;
      return api.adminUpdateSettings(body);
    },
    onSuccess: (res) => {
      setMsg('Saved');
      setForm((f) => ({ ...f, smtp_password: '' }));
      setPasswordSet(!!res?.settings?.smtp_password_set);
    },
    onError: (err) => setMsg(err.message),
  });

  if (isLoading) return <PageSkeleton />;

  return (
    <div className="max-w-2xl">
      <h1 className="mb-6 text-2xl font-semibold">Platform settings</h1>

      <Card>
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-400">General</h2>
        <div className="grid gap-3">
          <Input label="Site title" value={form.site_title} onChange={set('site_title')} />
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-slate-700">Default locale</span>
            <select value={form.default_locale} onChange={set('default_locale')} className="w-full rounded-dc-sm border border-slate-400 px-3 py-2 text-sm">
              <option value="en">English</option>
              <option value="bn">বাংলা</option>
            </select>
          </label>
        </div>
      </Card>

      <Card className="mt-4">
        <h2 className="mb-1 text-sm font-semibold uppercase tracking-wide text-slate-400">SMTP / Email</h2>
        <p className="mb-3 text-xs text-slate-500">Outbound mail server used for booking confirmations, cancellations and reminders.</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <Input label="SMTP host" placeholder="mail.example.com" value={form.smtp_host} onChange={set('smtp_host')} />
          <Input label="SMTP port" type="number" placeholder="465" value={form.smtp_port} onChange={set('smtp_port')} />
          <Input label="SMTP username" placeholder="info@example.com" value={form.smtp_user} onChange={set('smtp_user')} />
          <Input
            label="SMTP password"
            type="password"
            placeholder={passwordSet ? '•••••••• (leave blank to keep)' : 'Enter password'}
            value={form.smtp_password}
            onChange={set('smtp_password')}
          />
        </div>
      </Card>

      <div className="mt-4 flex items-center gap-3">
        <Button onClick={() => save.mutate()} disabled={save.isPending}>{save.isPending ? <Spinner /> : 'Save configurations'}</Button>
        {msg && <span className="text-sm text-slate-500">{msg}</span>}
      </div>
    </div>
  );
}
